import { Flashbar, Spinner } from '@amzn/awsui-components-react';
import { createContext, ReactNode, useContext, useState } from 'react';
import { useCodeAuthorization } from '../hooks/useCodeAuthorization';
import { signin } from '../lib/auth';
import { useConfig } from './ConfigContext';
import { IdPs } from '../constants/identityProviders';
import jwtDecode from 'jwt-decode';
export type AdminAuthState = {
    token: string | null;
};

export const AdminAuthContext = createContext<AdminAuthState>({
    token: null,
});

export const useAdminAuth = (): AdminAuthState => {
    return useContext(AdminAuthContext);
};

export const AdminAuthBoundary = ({ children }: { children: ReactNode }) => {
    const [adminAuthState, setAdminAuthState] = useState<AdminAuthState>({ token: null });
    const codeAuthState = useCodeAuthorization();
    const config = useConfig();

    if (!adminAuthState.token) {
        if (codeAuthState.loading) {
            return <Spinner />;
        } else {
            if (codeAuthState.err) {
                return (
                    <Flashbar
                        items={[
                            {
                                header: 'Failed to authenticate as Midway user.',
                                type: 'error',
                                dismissible: false,
                                id: 'message_1',
                            },
                        ]}
                    />
                );
            } else if (codeAuthState.confirmedAccount) {
                if (
                    codeAuthState.confirmedAccount.idp !== IdPs.AmazonFederate ||
                    !codeAuthState.confirmedAccount.token
                ) {
                    return (
                        <Flashbar
                            items={[
                                {
                                    header: 'You must login as a Midway user.',
                                    content: 'Reload the page to try again.',
                                    type: 'error',
                                    dismissible: false,
                                    id: 'message_1',
                                },
                            ]}
                        />
                    );
                }

                // Check for custom:ldap_groups claim in JWT token
                try {
                    const decoded = jwtDecode<any>(codeAuthState.confirmedAccount.token);
                    const userLdapGroups = decoded['custom:ldap_groups'];

                    if (!userLdapGroups) {
                        return (
                            <Flashbar
                                items={[
                                    {
                                        header: 'Unauthorized Access',
                                        content: 'You do not have the required permissions to access this page.',
                                        type: 'error',
                                        dismissible: false,
                                        id: 'unauthorized',
                                    },
                                ]}
                            />
                        );
                    }
                } catch (error) {
                    return (
                        <Flashbar
                            items={[
                                {
                                    header: 'Authentication Error',
                                    content: 'Invalid token format.',
                                    type: 'error',
                                    dismissible: false,
                                    id: 'token_error',
                                },
                            ]}
                        />
                    );
                }

                setAdminAuthState({
                    token: codeAuthState.confirmedAccount.token,
                });
            } else {
                signin(config, IdPs.AmazonFederate);
            }
            return <></>;
        }
    }
    return <AdminAuthContext.Provider value={adminAuthState}>{children}</AdminAuthContext.Provider>;
};
